import { useCallback, useEffect, useRef, useState } from 'react';

import { Dialog as BaseDialog } from '@base-ui/react/dialog';
// Straight from Phosphor, not from `icons`. That barrel exists so the visual refresh can swap an MUI
// glyph for its Phosphor counterpart (HNS-262) — a DS 2.0 component is already on the far side of that
// swap, and going through it would pull MUI into a layer that is deliberately free of it. The deep
// import path is the convention `icons/phosphorMapping.ts` already uses.
import { XIcon } from '@phosphor-icons/react/X';
import { IconButton } from 'components/Button';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import {
  dialogBackdropRecipe,
  dialogBodyContentRecipe,
  dialogBodyRecipe,
  dialogDescriptionRecipe,
  dialogFadeRecipe,
  dialogFooterRecipe,
  dialogHeaderRecipe,
  dialogPopupRecipe,
  dialogTitleRecipe,
} from './Dialog.recipe';

import type { DialogProps } from './types';

/**
 * Design System 2.0 dialog.
 *
 * Ported from the Figma dialog set (file 5aSkE0U4TeWbuIZDvccHjc, node 544:20045), page
 * `Dialog [Reviewed][Semantic]`, re-read 2026-09-21. `size` mirrors the Figma variant axis, whose
 * values are now bare `sm` / `md` / `lg` — the widths moved into the set's description (360 / 600 /
 * 900) rather than living in the variant names as "Size=md - 600".
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor via the token layer in
 * `components/styles/design-system.css` — carried across the portal by `useInheritedDarkMode`, since
 * a popup mounted on `<body>` has no ancestors left to inherit from.
 *
 * The layout is owned here rather than composed by the caller, and that is the point of the
 * component: the header and footer are siblings of the scroll area, not children of it, so the
 * actions cannot scroll out of reach no matter what goes in the body. That is the bug the redesign
 * fixes (HNS-337) and it is not something a call site should be able to reintroduce.
 *
 * Built on Base UI's `Dialog`, whose default `modal` behaviour supplies the whole accessibility
 * contract: focus moves in and is trapped, returns to the opener on close, Escape closes, the rest of
 * the page goes inert, and the popup is announced as a dialog named by its `Title`.
 */
export const Dialog = ({
  open,
  onOpenChange,
  onOpenChangeComplete,
  size,
  title,
  description,
  children,
  footer,
  showCloseButton,
  closeLabel = 'Close',
  closeOnEscape = true,
  closeOnBackdropClick = true,
  initialFocus,
  finalFocus,
  className,
  'data-testid': testId,
}: DialogProps) => {
  const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(open);

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const bodyContentRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [overflow, setOverflow] = useState({ top: false, bottom: false });

  const syncOverflow = useCallback(() => {
    const body = bodyRef.current;

    if (!body) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = body;

    setOverflow({
      top: scrollTop > 0,
      // Rounded up because a fractional scroll position — which a trackpad or a zoomed page produces
      // routinely — otherwise leaves the bottom fade showing a pixel short of the end, forever.
      bottom: Math.ceil(scrollTop + clientHeight) < scrollHeight,
    });
  }, []);

  /**
   * A callback ref, NOT a `useEffect` reading `bodyRef.current`.
   *
   * Base UI mounts the popup's subtree in a later commit than this component's own mount effect, so an
   * effect finds the ref null — and with dependencies that never change again, never gets a second
   * chance. That left the fades dead on arrival, reacting only to `onScroll` and never to the state
   * the dialog opened in. A callback ref runs exactly when the node attaches and detaches, whenever
   * that turns out to be.
   *
   * The content div's ref is already populated here: React attaches refs bottom-up, so the child is
   * set before its parent's callback fires.
   */
  const attachBody = useCallback(
    (node: HTMLDivElement | null) => {
      bodyRef.current = node;

      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) {
        // Closed. Cleared so a reopen cannot flash the previous dialog's fades before its first sync.
        setOverflow({ top: false, bottom: false });

        return;
      }

      syncOverflow();

      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      // Both boxes matter, for different reasons: the BODY resizing means the space changed (the
      // window, or a wrapping title stealing a line), while the CONTENT resizing means there is more
      // or less to scroll. Watching only the body would miss a list that grew inside a dialog already
      // pinned to its maximum height.
      const observer = new ResizeObserver(syncOverflow);
      observer.observe(node);

      if (bodyContentRef.current) {
        observer.observe(bodyContentRef.current);
      }

      observerRef.current = observer;
    },
    [syncOverflow]
  );

  // React 18 has no ref-callback cleanup, and an unmount while open (a route change, say) never calls
  // `attachBody(null)`. Without this the observer outlives the dialog.
  useEffect(() => () => observerRef.current?.disconnect(), []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, eventDetails: BaseDialog.Root.ChangeEventDetails) => {
      // A dialog that must be answered has to survive Escape. Cancelling the event rather than
      // swallowing the callback is what keeps Base UI's own state in step with ours.
      if (!closeOnEscape && eventDetails.reason === 'escape-key') {
        eventDetails.cancel();

        return;
      }

      onOpenChange(nextOpen);
    },
    [closeOnEscape, onOpenChange]
  );

  return (
    <>
      {/*
        The sentinel. Marks where this dialog really lives in the React tree so the portal below can
        inherit the ambient colour scheme; `hidden` keeps it out of layout and out of the a11y tree.
      */}
      <span ref={sentinelRef} hidden />

      <BaseDialog.Root
        disablePointerDismissal={!closeOnBackdropClick}
        open={open}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={onOpenChangeComplete}
      >
        <BaseDialog.Portal className={inheritedDarkClass}>
          {/*
            `forceRender` because Base UI suppresses a NESTED dialog's backdrop outright —
            `DialogBackdrop` is mounted with `enabled: forceRender || !nested`, and `nested` is true
            for any dialog whose `Root` has another `Root` above it in the React tree.

            That default belongs to Base UI's own stacked-dialog treatment, where the PARENT scales
            and slides back (it publishes `--nested-dialogs` and `data-nested-dialog-open` for
            exactly that) so a second scrim would double-dim a surface already pushed away. This
            component does not implement that treatment, so suppressing the scrim left nothing at
            all to separate the two layers: a confirmation raised from inside a dialog drew the same
            `--ds-dialog-surface` at the same width in the same centred position, and read as a band
            of content spliced into the middle of its parent rather than as a dialog over it
            (HNS-367 — the "Remove this background?" confirm inside Media Settings).

            Unconditional rather than gated on `nested`: every dialog this component draws is modal,
            so every one of them wants its scrim. Two open dialogs mean two scrims, which is the
            layering being told truthfully.
          */}
          {/*
              `onClick` on BOTH the scrim and the popup below. The portal moves them out of the DOM
              tree but not out of the REACT tree, and synthetic events propagate up the latter — so
              a press anywhere in the dialog reaches whatever `onClick` the CALL SITE sits inside.
              Not hypothetical: `EventsTableActionsCell` renders the Duplicate confirmation
              (`useEventRepeat` -> `common/hooks/useConfirmationModal` -> this component) as a child
              of a table row whose `onClick` navigates, so dismissing it opened the event page.

              Two handlers rather than one because the legacy MUI dialog
              (`common/components/Dialog/Dialog.tsx`) guarded this on its root, which spanned the
              backdrop as well; here the backdrop and the popup are siblings inside the portal.

              Dismissal is untouched — Base UI listens for presses outside on `document` in the
              CAPTURE phase, which runs before the event reaches its target.
          */}
          <BaseDialog.Backdrop
            forceRender
            className={dialogBackdropRecipe()}
            onClick={(event) => event.stopPropagation()}
          />

          <BaseDialog.Popup
            className={cn(dialogPopupRecipe({ size }), className)}
            onClick={(event) => event.stopPropagation()}
            // On the popup rather than the scroll area itself, for two reasons: it is the element a
            // reviewer already has in hand when debugging, and `|| undefined` matters — an attribute
            // set to the string "false" still matches `[data-overflow-top]` and would pin both fades on.
            data-overflow-bottom={overflow.bottom || undefined}
            data-overflow-top={overflow.top || undefined}
            data-testid={testId}
            finalFocus={finalFocus}
            initialFocus={initialFocus}
          >
            <header className={dialogHeaderRecipe()}>
              <BaseDialog.Title className={dialogTitleRecipe()}>{title}</BaseDialog.Title>

              {showCloseButton && (
                /*
                  `2xs`, NOT `xs`. The set draws this as `Size=ExtraSmall, Type=Muted, iconOnly=Yes`
                  — a 24px box with a 16px glyph and 4px of padding — and its description names it
                  as "the same ExtraSmall Muted icon button Alert uses", which is exactly what
                  `Alert` passes. The code scale is one step offset from Figma's: `2xs` is
                  ExtraSmall (24px) and `xs` is Figma's SMALL (32px). This read `xs` because it was
                  written when `xs` WAS the ExtraSmall cell; the Button port re-keyed the scale
                  (`3xs`/`2xs`/`xs`/`regular`/`large` against Tiny/ExtraSmall/Small/Medium/Large,
                  all five verified against the set on 2026-09-21) and left this pointing at a
                  32px button. Nothing went red, because both sizes exist and both render.

                  The 24px cell is under the 44px touch target the product's accessibility rules
                  ask for — in Figma as much as here, and confirmed with Edo on 2026-09-21 as the
                  size to ship. Flagged rather than silently grown, which is the same answer
                  `TextInput`, `TextArea` and `DatePicker` reached for their own 24px controls:
                  growing this one alone would fork the pattern in a single component against a
                  settled DS-wide position. It wants fixing across the design system, not here.
                */
                <BaseDialog.Close
                  render={
                    <IconButton appearance="tertiary" aria-label={closeLabel} fill="default" size="2xs">
                      <XIcon />
                    </IconButton>
                  }
                />
              )}
            </header>

            {/*
              `data-dialog-body` is the stable handle for the one scrollable region — the attribute a
              story, a Playwright page object or a debugging session reaches for. A utility class is not
              that: `overflow-y-auto` is an implementation detail of the recipe and may move.
            */}
            <div ref={attachBody} className={dialogBodyRecipe()} data-dialog-body onScroll={syncOverflow}>
              <span aria-hidden className={dialogFadeRecipe({ edge: 'top' })} />

              <div ref={bodyContentRef} className={dialogBodyContentRecipe()}>
                {description && (
                  <BaseDialog.Description className={dialogDescriptionRecipe()}>{description}</BaseDialog.Description>
                )}

                {children}
              </div>

              <span aria-hidden className={dialogFadeRecipe({ edge: 'bottom' })} />
            </div>

            {footer && <footer className={dialogFooterRecipe()}>{footer}</footer>}

            {/*
              Base UI's documented requirement for a modal popup: render a `Dialog.Close` inside it so
              a touch screen-reader user, who cannot press Escape, still has a way out. The footer's
              buttons are the caller's and might all be "Save", so nothing here can assume one closes.

              Last in the popup so it is the final tab stop rather than the first thing a keyboard user
              meets, and skipped only when the dialog is undismissable outright — one that must be
              answered should not quietly grow a hidden dismissal. If EITHER Escape or the backdrop
              still works, the hatch is just that same exit made reachable.
            */}
            {!showCloseButton && (closeOnEscape || closeOnBackdropClick) && (
              <BaseDialog.Close className="sr-only">{closeLabel}</BaseDialog.Close>
            )}
          </BaseDialog.Popup>
        </BaseDialog.Portal>
      </BaseDialog.Root>
    </>
  );
};
